import { useState } from 'react';
import { X, FileText, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../api/axios';
import { buildExportTitle, buildExportFilename } from '../../utils/exportTitle';

const ALL_COLUMNS = [
  { key: 'user_id', label: 'User ID' },
  { key: 'full_name', label: 'Name' },
  { key: 'father_husband_name', label: 'Father/Husband' },
  { key: 'gender', label: 'Gender' },
  { key: 'age', label: 'Age' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'caste_category', label: 'Caste Cat.' },
  { key: 'caste', label: 'Caste' },
  { key: 'profession', label: 'Profession' },
  { key: 'voter_id', label: 'Voter ID' },
  { key: 'phone_number', label: 'Phone' },
  { key: 'committee_type', label: 'Committee' },
  { key: 'position', label: 'Position' },
  { key: 'affiliated_wing', label: 'Wing' },
  { key: 'level', label: 'Level' },
  { key: 'district', label: 'District' },
  { key: 'assembly_name', label: 'Assembly' },
  { key: 'pc', label: 'PC' },
  { key: 'mandal', label: 'Mandal' },
  { key: 'panchayat', label: 'Panchayat' },
  { key: 'status', label: 'Status' },
  { key: 'created_date', label: 'Created' },
];

const DEFAULT_SELECTED = [
  'full_name', 'status', 'committee_type',
  'position', 'mandal', 'phone_number', 'voter_id',
];

const formatDateShort = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB');
};

// Load an image URL into a base64 data URL for embedding in the PDF
const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const size = 80;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          // center-crop square
          const s = Math.min(img.width, img.height);
          const sx = (img.width - s) / 2;
          const sy = (img.height - s) / 2;
          ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch {
      resolve(null);
    }
  });

export default function PdfExportModal({ open, onClose, filters }) {
  const [selected, setSelected] = useState(() => new Set(DEFAULT_SELECTED));
  const [includePhoto, setIncludePhoto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');

  if (!open) return null;

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(ALL_COLUMNS.map((c) => c.key)));
  const selectNone = () => setSelected(new Set());

  const orderedColumns = ALL_COLUMNS.filter((c) => selected.has(c.key));

  const handleDownload = async () => {
    if (orderedColumns.length === 0) return;
    setLoading(true);
    try {
      setProgress('Fetching records...');
      const params = { ...filters, format: 'json' };
      if (selected.has('user_id')) params.includeUserId = 'true';
      const res = await api.get('/registrations/export', { params });
      const data = Array.isArray(res.data) ? res.data : [];

      // Preload photos if needed
      const photoMap = new Map();
      if (includePhoto && data.length > 0) {
        setProgress(`Loading ${data.length} photos...`);
        const results = await Promise.all(
          data.map((r) => loadImageAsDataUrl(r.photo_url).then((d) => [r.id, d]))
        );
        results.forEach(([id, d]) => photoMap.set(id, d));
      }

      setProgress('Generating PDF...');

      // Landscape A3 gives plenty of horizontal room so every selected column
      // stays on a single line per row.
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });

      const head = [[
        '#',
        ...(includePhoto ? ['Photo'] : []),
        ...orderedColumns.map((c) => c.label),
      ]];

      const body = data.map((r, i) => {
        const row = [String(i + 1)];
        if (includePhoto) row.push(''); // filled via didDrawCell
        for (const c of orderedColumns) {
          let v = r[c.key];
          if (c.key === 'created_date') v = formatDateShort(v);
          row.push(v == null || v === '' ? '-' : String(v));
        }
        return row;
      });

      // Dynamic font size so many columns still fit on one line
      const colCount = orderedColumns.length + 1 + (includePhoto ? 1 : 0);
      const fontSize = colCount >= 18 ? 6 : colCount >= 14 ? 7 : colCount >= 10 ? 8 : 9;

      const photoColIdx = includePhoto ? 1 : -1;

      autoTable(doc, {
        head,
        body,
        startY: 48,
        theme: 'grid',
        styles: {
          fontSize,
          cellPadding: 3,
          overflow: 'linebreak',
          valign: 'middle',
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: [76, 29, 149],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'left',
        },
        alternateRowStyles: { fillColor: [250, 250, 252] },
        columnStyles: includePhoto
          ? { [photoColIdx]: { cellWidth: 34, minCellHeight: 34, halign: 'center' } }
          : {},
        tableWidth: 'auto',
        horizontalPageBreak: false,
        margin: { top: 48, left: 20, right: 20, bottom: 24 },
        didDrawPage: (d) => {
          doc.setFontSize(14);
          doc.setTextColor(40);
          doc.text(buildExportTitle(filters), 20, 28);
          doc.setFontSize(9);
          doc.setTextColor(120);
          const meta = `Generated: ${new Date().toLocaleString()}   |   Records: ${data.length}`;
          doc.text(meta, 20, 42);
          const pageStr = `Page ${d.pageNumber}`;
          doc.text(pageStr, doc.internal.pageSize.getWidth() - 60, 28);
        },
        didDrawCell: (d) => {
          if (
            includePhoto &&
            d.section === 'body' &&
            d.column.index === photoColIdx
          ) {
            const reg = data[d.row.index];
            const img = photoMap.get(reg?.id);
            if (img) {
              const size = Math.min(d.cell.width, d.cell.height) - 4;
              const x = d.cell.x + (d.cell.width - size) / 2;
              const y = d.cell.y + (d.cell.height - size) / 2;
              try {
                doc.addImage(img, 'JPEG', x, y, size, size);
              } catch {
                /* ignore */
              }
            }
          }
        },
      });

      doc.save(buildExportFilename(filters, 'pdf'));
      onClose();
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-ysrcp-600" />
            <h3 className="text-lg font-semibold text-gray-900">Export as PDF</h3>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">
              Columns ({selected.size}/{ALL_COLUMNS.length})
            </h4>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                type="button"
                className="text-xs text-ysrcp-600 hover:text-ysrcp-700 font-medium"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={selectNone}
                type="button"
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                None
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
            {ALL_COLUMNS.map((c) => (
              <label
                key={c.key}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 hover:border-ysrcp-200 hover:bg-ysrcp-50/40 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.key)}
                  onChange={() => toggle(c.key)}
                  className="rounded text-ysrcp-600 focus:ring-ysrcp-500"
                />
                <span className="text-gray-700">{c.label}</span>
              </label>
            ))}
          </div>

          <label className="flex items-start gap-3 px-3 py-3 rounded-lg border border-gray-100 bg-gray-50/60 cursor-pointer">
            <input
              type="checkbox"
              checked={includePhoto}
              onChange={(e) => setIncludePhoto(e.target.checked)}
              className="mt-0.5 rounded text-ysrcp-600 focus:ring-ysrcp-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-800">Include user photo</div>
              <div className="text-xs text-gray-500">
                Embeds each candidate's photo in the first column. Slower for large exports.
              </div>
            </div>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500 min-h-[1rem]">{progress}</div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={loading || selected.size === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-ysrcp-500 text-white text-sm font-medium hover:bg-ysrcp-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" /> Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
